// MODELLO DELTA delle varianti regionali (task #171, ratifica Luca 2026-08-07:
// «Ci serve anche en-GB oltre a pt-BR e es-MX»).
//
// Una VARIANTE non è una lingua nuova: è la stessa lingua di una locale BASE
// con poche stringhe scritte diversamente. Duplicare l'intero dizionario
// costerebbe 3 × 3200 chiavi di debito che si sbilanciano al primo round che
// tocca la base, quindi la variante DICHIARA SOLO LE DIFFERENZE e il dizionario
// runtime nasce da `mergeDelta(base, delta)`. Conseguenze volute:
//   - una chiave NUOVA nella base arriva gratis alla variante, già tradotta;
//   - la parità N-aria si misura su base + delta, mai sul solo delta (sennò
//     avremmo tre locale rosse per sempre);
//   - il sidecar anti-stantie di una variante traccia SOLO le chiavi del delta:
//     per tutte le altre vale il sidecar della base, che è la traduzione vera.
//
// Il file non importa NIENTE di proposito: lo leggono anche gli script node
// (scripts/i18n-status.mjs, scripts/split-dictionaries.mjs) importandolo per
// percorso assoluto, e un import relativo senza estensione lì non si risolve.

// Le varianti e la loro base. `dictionaries.ts` la ri-tipizza su `Lang`, così
// una voce con un tag non registrato non compila.
export const VARIANT_BASE = {
  'en-GB': 'en-US',
  'es-MX': 'es-ES',
  'pt-BR': 'pt-PT'
} as const

// Un delta: la stessa forma della base, ma ogni ramo e ogni foglia opzionali.
// Le stringhe restano stringhe (niente `Partial` su di esse) e le foglie
// plurali, che sono oggetti di categorie CLDR, si possono sovrascrivere anche
// per singola categoria — il merge riempie il resto dalla base.
export type DeepPartial<T> = T extends string ? T : { [K in keyof T]?: DeepPartial<T[K]> }

const isBranch = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object'

const mergeBranches = (base: Record<string, unknown>, delta: Record<string, unknown>): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...base }

  for (const [key, value] of Object.entries(delta)) {
    const current = merged[key]

    merged[key] = isBranch(current) && isBranch(value) ? mergeBranches(current, value) : value
  }

  return merged
}

// Base + delta, in profondità. La copia è nuova: la base resta intatta, così
// due varianti della stessa base non si vedono a vicenda.
//
// Il parametro `delta` NON è tipizzato `DeepPartial<T>`: con T generico quello
// resta un tipo condizionale differito e TypeScript rifiuta qualunque oggetto
// concreto. Il controllo vero delle chiavi sta un livello più in basso, nei
// file di sezione, dove `DeepPartial<typeof enUS>` è su un tipo CONCRETO e una
// chiave inventata non compila; qui resta il vincolo che i rami di primo
// livello del delta siano rami della base.
export const mergeDelta = <T extends object>(base: T, delta: { [K in keyof T]?: unknown }): T => {
  const merged: T = { ...base }

  for (const [key, value] of Object.entries(delta)) {
    const current: unknown = Reflect.get(merged, key)

    Reflect.set(merged, key, isBranch(current) && isBranch(value) ? mergeBranches(current, value) : value)
  }

  return merged
}
